/**
 * Auto-Grading Configuration
 * Centralized configuration for auto-gradable activity types
 */

import type { SlideType } from '../supabase/client';

/**
 * List of activity types that support automatic grading
 * 
 * These activities are automatically graded when submitted:
 * - Multiple Choice: Grade based on correct answer index
 * - True/False: Grade based on boolean correctness
 * - Fill in the Blanks: Grade based on exact or case-insensitive match
 * - Matching Pairs: Grade based on correct pairings
 * - Time to Climb: Grade based on quiz answers
 * - Drag & Drop: Grade based on correct placements
 * - Sequencing: Grade based on correct order
 * - Quiz: Grade based on multiple questions
 */
export const AUTO_GRADABLE_ACTIVITIES: SlideType[] = [
  'multiple_choice',
  'true_false',
  'fill_blanks',
  'matching_pairs',
  'time_to_climb',
  'drag_drop',
  'sequencing',
  'quiz',
];

/**
 * Check if an activity type is auto-gradable
 */
export function isAutoGradable(slideType: SlideType): boolean {
  return AUTO_GRADABLE_ACTIVITIES.includes(slideType);
}

/**
 * Get display name for grading status
 */
export function getGradingStatusLabel(slideType: SlideType, graded: boolean): string {
  if (isAutoGradable(slideType)) {
    return 'Auto-graded';
  }
  return graded ? 'Manually graded' : 'Pending';
}

/**
 * Activities that require manual grading
 */
export const MANUAL_GRADING_ACTIVITIES: SlideType[] = [
  'open_ended',
  'draw_it',
  'label_diagram',
  'collaborate_board',
  'word_cloud',
  'poll',
];

/**
 * Check if an activity requires manual grading
 */
export function requiresManualGrading(slideType: SlideType): boolean {
  return MANUAL_GRADING_ACTIVITIES.includes(slideType);
}

/**
 * Get the default max points for a slide type.
 * Must stay in sync with slideRegistry.ts defaultContent.points values.
 */
export function getDefaultPoints(slideType: string): number {
  switch (slideType) {
    case 'quiz':          return 100;
    case 'time_to_climb': return 50;
    default:              return 10;
  }
}

/**
 * Resolve the max points for a slide, using the slide's configured points
 * or falling back to the type-specific default.
 */
export function resolveMaxPoints(slide: { type: string; content?: any }): number {
  const pts = slide.content?.points ?? slide.content?.maxPoints;
  if (typeof pts === 'number' && pts > 0) return pts;
  return getDefaultPoints(slide.type);
}
